import { QueryClient, useQuery, useQueryClient } from "react-query";
import { firestore } from "@/services/firestore";
import { CustomerUpdateStatus } from "@/types/v2";
import { WhereFilterOp } from "firebase/firestore";

interface UseTotalCustomerUpdatesParams {
  shopId: string;
  userId?: string;
  status?: CustomerUpdateStatus;
  cardNumberFilter?: string;
  enabled?: boolean;
}

// Helper function to build search options
const buildSearchOptions = (
  userId?: string,
  status?: CustomerUpdateStatus,
  cardNumberFilter?: string
): Array<{
  searchParam: string;
  opsString: WhereFilterOp;
  equality: unknown;
}> => {
  const searchOptions = [];
  if (userId) {
    searchOptions.push({
      searchParam: "userId",
      opsString: "==" as const,
      equality: userId,
    });
  }
  if (status) {
    searchOptions.push({
      searchParam: "status",
      opsString: "==" as const,
      equality: status,
    });
  }
  if (cardNumberFilter && cardNumberFilter.trim()) {
    const trimmedFilter = cardNumberFilter.trim();
    searchOptions.push({
      searchParam: "cardNumber",
      opsString: ">=" as const,
      equality: trimmedFilter,
    });
    searchOptions.push({
      searchParam: "cardNumber",
      opsString: "<" as const,
      equality: trimmedFilter + "\uf8ff",
    });
  }
  return searchOptions;
};

const invalidateTotalCustomerUpdatesQuery = (queryClient: QueryClient) => {
  // This function can be used to invalidate the total customerUpdates query
  // when a user is added or removed, ensuring the data is fresh.
  queryClient.invalidateQueries("totalCustomerUpdates");
};

const totalCustomerUpdatesQuery = (params: UseTotalCustomerUpdatesParams) => ({
  queryKey: [
    "totalCustomerUpdates",
    params.shopId,
    params.userId,
    params.status,
    params.cardNumberFilter,
  ],
  queryFn: async () => {
    const { shopId, userId, status, cardNumberFilter } = params;

    // Build search options for filters
    const searchOptions = buildSearchOptions(userId, status, cardNumberFilter);

    // If no filters are applied, use the simple count method
    if (searchOptions.length === 0) {
      return await firestore.getDocumentCount(
        `/shops/${shopId}/customerUpdates`
      );
    }

    // For filtered queries, we need to get the documents and count them
    // since Firestore doesn't support counting with complex queries directly
    return await firestore.getFilteredDocumentCount(
      `/shops/${shopId}/customerUpdates`,
      searchOptions
    );
  },
  refetchOnWindowFocus: true,
});

export const useTotalCustomerUpdatesQuery = (
  params: UseTotalCustomerUpdatesParams
) => {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    ...totalCustomerUpdatesQuery(queryParams),
    enabled,
  });
};

export const useInvalidateTotalCustomerUpdatesQuery = () => {
  const queryClient = useQueryClient();
  return () => invalidateTotalCustomerUpdatesQuery(queryClient);
};
